/**
 * Authentication API
 */
import apiClient from './client';

export interface UserRegisterRequest {
  username: string;
  password: string;
  email: string;
  full_name: string;
  role: 'student' | 'teacher';
  school_id: string;
}

export interface UserLoginRequest {
  username: string;
  password: string;
}

export interface UserResponse {
  username: string;
  email: string;
  full_name: string;
  role: 'student' | 'teacher' | 'admin';
  school_id?: string;
  school_name?: string;
  created_at: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
  user: UserResponse;
}

/**
 * Register a new user
 */
export const registerUser = async (data: UserRegisterRequest): Promise<TokenResponse> => {
  const response = await apiClient.post<TokenResponse>('/auth/register', data);
  return response.data;
};

/**
 * Login user
 */
export const loginUser = async (data: UserLoginRequest): Promise<TokenResponse> => {
  const response = await apiClient.post<TokenResponse>('/auth/login', data);
  return response.data;
};

/**
 * Logout user
 */
export const logoutUser = async (): Promise<void> => {
  await apiClient.post('/auth/logout');
};

/**
 * Get current user info
 */
export const getCurrentUser = async (): Promise<UserResponse> => {
  const response = await apiClient.get<UserResponse>('/auth/me');
  return response.data;
};

/**
 * Get user by username
 */
export const getUserByUsername = async (username: string): Promise<UserResponse> => {
  const response = await apiClient.get<UserResponse>(`/auth/users/${username}`);
  return response.data;
};

// ============== Settings API ==============

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface UpdateProfileRequest {
  full_name?: string;
  email?: string;
}

/**
 * Change password
 */
export const changePassword = async (data: ChangePasswordRequest): Promise<void> => {
  await apiClient.put('/auth/change-password', data);
};

/**
 * Update profile (name, email)
 */
export const updateProfile = async (data: UpdateProfileRequest): Promise<UserResponse> => {
  const response = await apiClient.put<UserResponse>('/auth/profile', data);
  return response.data;
};

/**
 * Delete account (requires password confirmation)
 */
export const deleteAccount = async (password: string): Promise<void> => {
  await apiClient.delete('/auth/account', { data: { password } });
};

// ============== Forgot Password / Username API ==============

/**
 * Forgot username — look up by email
 */
export const forgotUsername = async (email: string): Promise<string> => {
  const response = await apiClient.post('/auth/forgot-username', { email });
  return response.data.message;
};

/**
 * Forgot password — request reset token
 */
export const forgotPassword = async (email: string): Promise<string> => {
  const response = await apiClient.post('/auth/forgot-password', { email });
  return response.data.message;
};

/**
 * Reset password using token
 */
export const resetPassword = async (token: string, newPassword: string): Promise<void> => {
  await apiClient.post('/auth/reset-password', { token, new_password: newPassword });
};
